import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFolder, deleteFolder, listFolders, renameFolder } from "../lib/api-client";
import { mailboxQueryKeys } from "../lib/query-keys";

export function useFolders(mailboxId: string | undefined) {
  return useQuery({
    queryKey: mailboxQueryKeys.folders(mailboxId ?? ""),
    queryFn: () => listFolders(mailboxId!),
    enabled: Boolean(mailboxId),
    staleTime: 5 * 60_000, // folders basically never change
  });
}

/**
 * Folder CRUD mutations (Phase 6). All three invalidate the folders list on
 * success rather than hand-patching the cache — this list is small and the
 * 5-minute staleTime above means a plain invalidate+refetch is cheap and
 * simple, same "invalidate, don't hand-splice" call the WebSocket handler
 * (use-mailbox-socket.ts) already makes for every other mutation in this
 * app. The WebSocket's own folder:created/updated/deleted handling covers
 * *other* connected clients/tabs; these mutations cover the tab that
 * actually made the change, which won't receive its own broadcast echoed
 * back meaningfully faster than this.
 */
export function useCreateFolder(mailboxId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => createFolder(mailboxId!, name),
    onSuccess: () => {
      if (!mailboxId) return;
      queryClient.invalidateQueries({ queryKey: mailboxQueryKeys.folders(mailboxId) });
    },
  });
}

export function useRenameFolder(mailboxId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ folderId, name }: { folderId: string; name: string }) => renameFolder(mailboxId!, folderId, name),
    onSuccess: () => {
      if (!mailboxId) return;
      queryClient.invalidateQueries({ queryKey: mailboxQueryKeys.folders(mailboxId) });
    },
  });
}

export function useDeleteFolder(mailboxId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (folderId: string) => deleteFolder(mailboxId!, folderId),
    onSuccess: () => {
      if (!mailboxId) return;
      // Any threads that were in the deleted folder moved to Archive
      // server-side — invalidate broadly, same reasoning as
      // use-mailbox-socket.ts's folder:deleted handling.
      queryClient.invalidateQueries({ queryKey: mailboxQueryKeys.folders(mailboxId) });
      queryClient.invalidateQueries({ queryKey: mailboxQueryKeys.threadsAll(mailboxId) });
    },
  });
}
